'use client';

import { useEffect } from 'react';

/**
 * Freeze the page behind a full-viewport overlay.
 *
 * A `fixed` overlay does not stop what is underneath from scrolling, so the
 * page keeps its own scrollbar next to the panel's — two bars on one screen,
 * and a wheel gesture that misses the panel silently scrolls a page nobody can
 * see. Radix dialogs get this for free; the hand-rolled overlays (the stacked
 * settings drill-down) have to ask for it.
 *
 * TWO things scroll, and both must be held: the document, and — on the desktop
 * layouts — the content column, which is its own `overflow-y-auto` element
 * (`[data-page-scroll]`) rather than the window. The visible bar on a wide
 * single-column window is the column's, so locking `body` alone fixes nothing.
 *
 * Reference-counted, because overlays stack (a drill-down inside a drill-down):
 * the last one to close is the one that restores scrolling, not the first.
 *
 * Hiding a scrollbar widens what it occupied, which would jerk everything still
 * visible around the overlay sideways, so any width the document actually gains
 * is paid back as padding. MEASURED, never assumed: `html` carries
 * `scrollbar-gutter: stable`, which keeps holding the gutter under
 * `overflow: hidden`, so at those widths the document gains nothing and paying
 * a scrollbar's worth of padding would itself shove the page sideways by half a
 * scrollbar (that was the bug). Comparing the html box before and after the
 * lock covers both cases with no branch. The column keeps its own gutter.
 */
let lockCount = 0;
let restore: (() => void) | null = null;

function acquire() {
  lockCount += 1;
  if (lockCount > 1) return;

  const { body, documentElement: html } = document;
  const previousOverflow = body.style.overflow;
  const previousPaddingEnd = body.style.paddingInlineEnd;
  const widthBefore = html.getBoundingClientRect().width;

  body.style.overflow = 'hidden';

  const gained = html.getBoundingClientRect().width - widthBefore;
  if (gained > 0) {
    body.style.paddingInlineEnd = `${gained}px`;
  }

  const columns = Array.from(
    document.querySelectorAll<HTMLElement>('[data-page-scroll]')
  ).map((element) => {
    const previous = element.style.overflowY;
    element.style.overflowY = 'hidden';
    return { element, previous };
  });

  restore = () => {
    body.style.overflow = previousOverflow;
    body.style.paddingInlineEnd = previousPaddingEnd;
    for (const { element, previous } of columns) {
      element.style.overflowY = previous;
    }
  };
}

function release() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;

  restore?.();
  restore = null;
}

export function useOverlayScrollLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    acquire();
    return release;
  }, [enabled]);
}
